import { baseApi, type MoneyDto } from '@shared/api/baseApi';

export const GIVEAWAY_REACTIONS = ['LIKE', 'LOVE', 'CARE', 'WOW', 'CELEBRATE', 'FIRE'] as const;
export type GiveawayReaction = (typeof GIVEAWAY_REACTIONS)[number];
export type GiveawayReactionCounts = Record<GiveawayReaction, number>;

export interface GiveawayDto {
  id: string;
  participantLimit: number;
  participantCount: number;
  winnerCount: number;
  status: 'LIVE' | 'DRAWN' | 'DELIVERED';
  winners: {
    position: number;
    firstName: string | null;
    username: string | null;
  }[];
  reactionCounts: GiveawayReactionCounts;
  userReaction: GiveawayReaction | null;
  startsAt: string;
  endsAt: string;
  product: {
    id: string;
    name: string;
    listPrice: MoneyDto;
    logoUrl: string;
  };
}

export interface GiveawayResponse {
  serverTime: string;
  clientReceivedAt: number;
  giveaway: GiveawayDto | null;
  giveaways?: GiveawayDto[];
}

export const giveawayApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getGiveaway: builder.query<GiveawayResponse, void>({
      query: () => '/giveaway',
      transformResponse: (response: Omit<GiveawayResponse, 'clientReceivedAt'>) => ({
        ...response,
        clientReceivedAt: Date.now(),
      }),
      providesTags: ['Giveaway'],
    }),
    participateInGiveaway: builder.mutation<
      Pick<GiveawayDto, 'participantCount' | 'participantLimit' | 'reactionCounts' | 'userReaction'>,
      { id: string; reaction: GiveawayReaction }
    >({
      query: ({ id, reaction }) => ({
        url: `/giveaways/${id}/participate`,
        method: 'POST',
        body: { reaction },
      }),
      invalidatesTags: ['Giveaway', 'AdminGiveaway'],
    }),
  }),
});

export const { useGetGiveawayQuery, useParticipateInGiveawayMutation } = giveawayApi;
